/**
 * BASS — GularLite acid/psy.
 *
 * Chirp provenance: Chirp/bittern.html (GULAR + the run grammar + the CALLS
 * table). The step grid, slide ties and accent lean are Bittern's; the
 * gesture-driven cutoff/resonance/drive and the free pinch note are this
 * instrument's.
 */

import { createBassEngine, type BassEngine } from '../audio/bass/gular-lite';
import { BASS_PATCHES, BASS_PATCH_IDS, cloneParams, type BassPatchId } from '../audio/bass/patches';
import { degreeSemis } from '../audio/common/pitch';
import type { Task } from '../audio/transport';
import { stepSeconds } from '../audio/transport';
import { BassAdapter } from '../mapping/adapters';
import { getExperiments } from '../mapping/config';
import type { BassIntent, ControlState, FeatureFrame } from '../mapping/types';
import { SCALES } from '../music/scales';
import { computeRuns, type Run } from '../music/runs';
import { button, chips, el, panel, slider, toggle } from '../ui/controls';
import type { Scene, SceneContext } from './types';

export function createBassScene(): Scene {
  let engine: BassEngine | null = null;
  let ctxRef: SceneContext | null = null;
  let task: Task | null = null;
  let patchId: BassPatchId = 'acid';
  let bpm: number = BASS_PATCHES.acid.bpm;
  let rootMidi = 33; // A1
  let runs = new Map<number, Run>();
  const adapter = new BassAdapter();
  let last: BassIntent | null = null;
  let stepNow = 0;
  let driveSlider: HTMLElement | null = null;

  /** A patch switch moves drive under the slider; make the UI tell the truth. */
  function syncDriveSlider(): void {
    const input = driveSlider?.querySelector('input');
    const read = driveSlider?.querySelector('b');
    if (input) input.value = String(patch().params.drive);
    if (read) read.textContent = patch().params.drive.toFixed(2);
  }

  const patch = (): typeof BASS_PATCHES[BassPatchId] => BASS_PATCHES[patchId];

  function rebuildRuns(): void {
    runs = new Map();
    for (const r of computeRuns([...patch().mask], [...patch().slide])) runs.set(r.start, r);
  }

  const midiAtStep = (s: number): number =>
    rootMidi + degreeSemis(SCALES[patch().scale].steps, patch().deg[s % 16]);

  function bookRun(run: Run, t: number, stepSec: number): void {
    if (!engine) return;
    const p = patch();
    const vel = 1 + (p.accent[run.start] ? engine.params.accentAmt : 0);
    const dur = Math.max(0.03, (run.steps.length - 1 + p.gate) * stepSec);
    const h = engine.noteOn(midiAtStep(run.steps[0]), t, dur, vel);
    run.steps.forEach((st, k) => {
      if (k > 0 && p.deg[st] !== p.deg[run.steps[k - 1]])
        h.glideTo(midiAtStep(st), t + k * stepSec, engine!.params.glide);
    });
  }

  function start(): void {
    if (!ctxRef || task) return;
    task = ctxRef.transport.addTask({
      bpm, division: 4,
      cb: (t, i) => {
        stepNow = i % 16;
        const run = runs.get(stepNow);
        if (run) bookRun(run, t, stepSeconds(bpm, 4));
      },
    });
  }
  function stop(): void { task?.cancel(); task = null; }

  return {
    id: 'bass',
    label: 'Bass',
    blurb: 'A 303-lineage acid line. Spread opens the filter, roll adds peak, height is the level, press fires a note off the grid.',
    padMode: 'xy',

    mount(host, ctx) {
      ctxRef = ctx;
      engine = createBassEngine(ctx.bus.ctx, ctx.bus.input, cloneParams(patch().params));
      rebuildRuns();
      const grid = el('div', { class: 'stepgrid', 'aria-hidden': 'true' });
      for (let i = 0; i < 16; i++) {
        grid.append(el('i', {
          class: `step${patch().mask[i] ? ' on' : ''}${patch().slide[i] ? ' slide' : ''}${patch().accent[i] ? ' acc' : ''}`,
        }));
      }
      const redrawGrid = (): void => {
        [...grid.children].forEach((c, i) => {
          c.className = `step${patch().mask[i] ? ' on' : ''}${patch().slide[i] ? ' slide' : ''}${patch().accent[i] ? ' acc' : ''}`;
        });
      };

      host.append(panel(patch().label, patch().note, [
        chips<BassPatchId>({
          label: 'Patch',
          options: BASS_PATCH_IDS.map((id) => ({ id, label: BASS_PATCHES[id].label, title: BASS_PATCHES[id].note })),
          value: patchId,
          onChange: (id) => {
            patchId = id;
            bpm = patch().bpm;
            Object.assign(engine!.params, cloneParams(patch().params));
            // Drive lives partly OUTSIDE params: the waveshaper curve and its
            // pre/post gains only move through setDrive. Without this, ACID's
            // curve survived a switch to PSY.
            engine!.setDrive(patch().params.drive);
            syncDriveSlider();
            rebuildRuns();
            redrawGrid();
            task?.setBpm(bpm);
            ctx.setStatus(`${patch().label} — ${patch().note}`);
          },
        }),
        grid,
        slider({ label: 'Tempo', min: 90, max: 175, step: 1, value: bpm, format: (v) => `${v} bpm`, onInput: (v) => { bpm = v; task?.setBpm(v); } }),
        slider({ label: 'Root', min: 21, max: 45, step: 1, value: rootMidi, format: (v) => `MIDI ${v}`, onInput: (v) => { rootMidi = v; } }),
        (driveSlider = slider({ label: 'Drive', min: 0, max: 1, step: 0.01, value: patch().params.drive, format: (v) => v.toFixed(2), onInput: (v) => engine?.setDrive(v) })),
        el('div', { class: 'row' }, [
          toggle('Run', false, (on) => { on ? start() : stop(); }, 'Start the bassline'),
          button('Hush', () => { stop(); ctx.bus.duck(); }, 'ghost'),
        ]),
      ]));
      ctx.setStatus(`${patch().label} — ${patch().note}`);
    },

    update(frame: FeatureFrame, control: ControlState) {
      if (!engine) return;
      const intent = adapter.update(frame, control);
      last = intent;
      engine.setCutoff(intent.cutoffHz);
      engine.setRes(intent.resonance);
      engine.setLevel(intent.level);
      // The trigger is the vocabulary's pinch — hysteresis, fist precedence
      // and all. Firing from raw press events bypassed every one of those
      // rules, which meant the pinch the whole mapping layer detects drove
      // nothing in this scene.
      //
      // Gate 4 rides here: quantized books the note on the scene's own grid
      // and samples the FRESHEST intent at fire-decision time — where the hand
      // travelled during the wait sets the note (the doc's option C sampling).
      // Immediate fires on the spot with the intent as armed.
      if (intent.trigger && ctxRef) {
        const play = (i: BassIntent, when: number): void => {
          engine?.noteOn(rootMidi + degreeSemis(SCALES[patch().scale].steps, i.degree) + i.octave * 12,
            when, 0.22, i.accent ? 1.5 : 1);
        };
        if (getExperiments().trigger === 'quantized') {
          ctxRef.transport.at(ctxRef.transport.nextGrid(bpm, 4), (t) => play(last ?? intent, t));
        } else {
          play(intent, ctxRef.bus.ctx.currentTime + 0.02);
        }
      }
    },

    selection() {
      if (!last) return null;
      return {
        count: 7,
        active: last.degree,
        label: `deg ${last.degree + 1}${last.octave ? ' +oct' : ''}`,
      };
    },

    readouts() {
      return [
        ['step', String(stepNow + 1)],
        ['cutoff', last ? `${Math.round(last.cutoffHz)} Hz` : '—'],
        ['peak', last ? last.resonance.toFixed(1) : '—'],
        ['level', last ? last.level.toFixed(2) : '—'],
      ];
    },

    unmount() { stop(); engine?.dispose(); engine = null; adapter.reset(); },
  };
}
